import Backdraft from "backdraft-app/src/backdraft";
import _ from "underscore";
import $ from "jquery";
import { history as History } from "backbone";

const REPORTING_COMPARE_COLUMN_SUFFIXES = [".compare", ".trend", ".difference"];

const BULK_COLUMN_HEADER_DEFAULT_WIDTH = 30;

const URL_PARAM_KEYS = ['sort_field', 'sort_dir'];

function DataTableSettingsManager(table, persistedSettings, options = {}) {
  this.table = table;
  this.persistedSettings = persistedSettings;
  this.saveOnChanges = _.has(options, 'saveOnChanges') ? options.saveOnChanges : true;
  this.saveOnVisibilityChanges = _.has(options, 'saveOnVisibilityChanges') ? options.saveOnVisibilityChanges : true;
  this.sortConfigInUrl = _.has(persistedSettings, "sortConfigInUrl") ? persistedSettings.sortConfigInUrl : false;

  this.table.on("render", this._onTableRender, this);

  if (this.persistedSettings) {
    this.persistedColumnWidths = this._getPersistedColumnWidths();

    // change sorting before table is rendered
    this._applyPersistedSorting();
  }
}

_.extend(DataTableSettingsManager.prototype, {
  getDefaults() {
    const columns = {};
    _.each(this._defaultColumns(), function(defaultCol) {
      if (defaultCol.id && this._findColumnById(defaultCol.id)) {
        let visible = true;
        if (_.isBoolean(defaultCol.visible)) { visible = defaultCol.visible; }
        if (_.isBoolean(defaultCol.defaultVisible)) { visible = defaultCol.defaultVisible; }
        columns[defaultCol.id] = {
          checked: visible,
          visible,
          required: defaultCol.required,
          notUserConfigurable: defaultCol.notUserConfigurable,
          attr: defaultCol.attr
        };
      }
    }, this);
    return columns;
  },

  restoreDefaults() {
    try {
      this.saveOnChanges = false;
      const defaultColumnOrder = [];

      _.each(this._defaultColumns(), function(defaultCol) {
        // restore visibility
        if (defaultCol.id && this._findColumnById(defaultCol.id)) {
          this.table.columnVisibility(defaultCol.id, !!defaultCol.visible);
        }
        const columnIdx = this.table.columnsConfig().indexOf(defaultCol);
        if (columnIdx !== -1) {
          defaultColumnOrder.push(columnIdx);
        }
      }, this);

      // restore order
      this.table.columnOrder(defaultColumnOrder);

      // restore sorting
      this.table.changeSorting(this.table.sorting);

      this._saveSettings({ restore_defaults: "1" });
    } finally {
      this.saveOnChanges = true;
    }
  },

  _resetSorting(table) {
    const sorting = this.sorting || table.sorting;
    table.changeSorting(sorting);
  },

  restoreDefaultColumnWidths() {
    if (this._canResize()) {
      this.persistedColumnWidths = _.mapObject(this.persistedColumnWidths, () => 0);
      _.each(this.table.dataTable.fnSettings().aoColumns, column => {
        $(column.nTh).css({ 'width': 0, 'min-width': 0 });
        $(column.nTh).find('.DataTables_sort_interceptor').css('max-width', 'none');
      });
      this._resetTableWidth();
    }
  },

  _applyPersistedSorting() {
    let savedSorting;
    const sortConfig = this._getSortSettings();
    if (_.isArray(sortConfig) && sortConfig.length) {
      savedSorting = sortConfig;
      let validColumns = true;
      const columnAttrs = _.pluck(this.table.columnsConfig(), 'attr');
      // replace column attributes with indexes
      savedSorting = _.map(savedSorting, sortColumn => {
        if (validColumns) {
          const colAttr = sortColumn[0];
          const colIdx = _.indexOf(columnAttrs, colAttr);
          if (colIdx !== -1) {
            return [colIdx, sortColumn[1]];
          } else {
            validColumns = false;
          }
        }
        return sortColumn;
      }, this);

      if (validColumns) {
        // valid saved sorting state, use it instead of default
        this.table.changeSorting(savedSorting);
      }
    }
  },

  _applyPersistedSettings() {
    this._applyPersistedVisibility();
    this._applyPersistedOrder();
  },

  _applyPersistedVisibility() {
    let column;
    if (this.persistedSettings) {
      _.each(this.persistedSettings.columnAdds, function(columnName) {
        column = this._findColumnByAttr(columnName);
        if (column) {
          this.table.columnVisibility(column.id, true);
        }

        _.each(REPORTING_COMPARE_COLUMN_SUFFIXES, suffix => {
          column = this._findColumnByAttr(columnName + suffix);
          if (column) {
            this.table.columnVisibility(column.id, true);
          }
        });
      }, this);

      _.each(this.persistedSettings.columnSubtracts, function(columnName) {
        column = this._findColumnByAttr(columnName);
        if (column) {
          this.table.columnVisibility(column.id, false);
        }

        _.each(REPORTING_COMPARE_COLUMN_SUFFIXES, suffix => {
          column = this._findColumnByAttr(columnName + suffix);
          if (column) {
            this.table.columnVisibility(column.id, false);
          }
        });
      }, this);
    }
  },

  _applyPersistedOrder() {
    let column;
    let columnIdx;
    let columnOrder;
    let columnsConfig;
    const columnPositions = {};
    const reorderedColumns = [];
    const reorderedColumnIndexes = [];

    if (this.persistedSettings) {
      columnOrder = this.persistedSettings.columnOrder;
      if (columnOrder && columnOrder.length > 0) {
        _.each(columnOrder, (columnName, index) => {
          columnPositions[columnName] = index;
        });

        // insert columns in the specified order
        _.each(columnOrder, function(columnName) {
          column = this._findColumnByAttr(columnName);
          if (column) {
            reorderedColumns.push(column);
          }
        }, this);

        columnsConfig = this.table.columnsConfig();

        // insert remaining columns with unspecified order
        _.each(columnsConfig, (column, index) => {
          if (columnPositions[column.attr] === undefined) {
            reorderedColumns.splice(index, 0, column);
          }
        });

        _.each(reorderedColumns, column => {
          columnIdx = columnsConfig.indexOf(column);
          reorderedColumnIndexes.push(columnIdx);
        });

        this.table.columnOrder(reorderedColumnIndexes);
      }
    }
  },

  _applyPersistedColumnWidths() {
    if (this._canResize()) {
      const self = this;
      this.table.columnElements();
      _.each(this.persistedColumnWidths, (columnWidth, columnKey) => {
        const column = self._findColumnById(columnKey);
        if (!column) {
          return;
        }

        const columnElement = $(self._findColumnElementFromId(column.id));
        const renderedWidth = columnElement.innerWidth();

        if (columnElement.length > 0 && columnWidth > 0) {
          columnElement.css({
            'width': columnWidth,
            'min-width': columnWidth
          });

          if (renderedWidth !== columnWidth) {
            // the -1 is to account for fractions of pixels - needed when moving to Bootstrap 4 given how fragile the calculations are
            //  and with 'rem' based font sizes; also other attempts were tried to avoid the - 1 and the hope is to
            //  revamp our table resizing in general
            const interceptorWidth = columnWidth - (self.table._colReorder._fnColumnPaddingWidth(columnElement) - 1);
            columnElement.find('.DataTables_sort_interceptor').css('max-width', interceptorWidth);
          }
        }
      });
    }
  },

  _canResize() {
    return !!this.persistedSettings && this.table.resizableColumns && !this.table.parent._columnGroupDefinitionDefined();
  },

  _defaultColumns() {
    return _.result(this.table.rowClass.prototype, "columns");
  },

  _findColumnById(id) {
    return this.table._columnManager.columnConfigForId(id);
  },

  _findColumnByAttr(columnAttr) {
    return _.find(this.table.columnsConfig(), column => column.attr === columnAttr);
  },

  _findDefaultColumnByAttr(columnAttr) {
    return _.find(this._defaultColumns(), column => column.attr === columnAttr);
  },

  _cacheSettingsState(state) {
    this.settingsState = state || this._getCurrentSettingsState();
  },

  _updatePersistedSettings() {
    const currentSettings = this._getCurrentSettingsState();
    this.persistedSettings.columnOrder = currentSettings.columnOrder;
    this.persistedSettings.columnOrderAndWidth = currentSettings.columnOrderAndWidth;
    this.persistedSettings.pageLength = currentSettings.pageLength;
    this.persistedColumnWidths = this._getPersistedColumnWidths();
  },

  _onTableRender() {
    if (this.persistedSettings && !this.settingsState) {
      this._applyPersistedSettings();
      this._cacheSettingsState();
      this.table.on("draw", this._onTableDraw, this);
      this.table._columnManager.on("change:visibility", this._onColumnVisibilityChange, this);
      this.table._columnManager.on("change:order", this._onColumnOrderChange, this);
      this.table.dataTable.on('mousedown', 'th', this._setResizingColumnFlag.bind(this));
      $(document).mouseup(this._onColumnWidthChange.bind(this));
      this.table.on('draw', this._onPageLengthChange, this);

      this._setBulkColumnHeaderDefaults();
    }
  },

  _setResizingColumnFlag(event) {
    if ($(event.target).css('cursor') === 'col-resize') {
      this.resizingColumnHeader = true;
    }
  },

  _getCurrentSettingsState() {
    return {
      columnVisibility: _.reduce(this.table.columnsConfig(), function(memo, column) {
        if (column.attr) {
          memo[column.attr] = this.table.columnVisibility(column.id);
        }
        return memo;
      }, {}, this),
      columnOrder: _.compact(_.map(this.table.columnsConfig(), column => column.attr)),
      columnOrderAndWidth: this._getCurrentColumnOrderAndWidth(),
      sorting: $.extend(true, [], this.table.dataTable.fnSettings().aaSorting),
      pageLength: this.table.pageLimit()
    };
  },

  _getPersistedColumnWidths() {
    const columnOrderAndWidth = this.persistedSettings.columnOrderAndWidth;
    if (columnOrderAndWidth && columnOrderAndWidth.length > 0) {
      const columnWidths = {};
      _.each(columnOrderAndWidth, columnNameAndWidth => {
        const splitValues = columnNameAndWidth.split('|');
        const columnName = splitValues[0];
        const columnWidth = parseInt(splitValues[1]) || 0;
        columnWidths[columnName] = columnWidth;
      });
      return columnWidths;
    } else {
      return {};
    }
  },

  _getCurrentColumnOrderAndWidth(columns) {
    const self = this;
    return _.compact(
      _.map(columns || this.table.columnsConfig(), column => {
        if (_.isUndefined(column.id)) {
          return null;
        }

        return `${column.id}|${self._getColumnWidth(column)}`;
      })
    );
  },

  _onTableDraw() {
    const currentSettings = this._getCurrentSettingsState();
    if (!_.isEqual(this.settingsState.sorting, currentSettings.sorting)) {
      const sortConfig = this._sortingConfigMapping(currentSettings.sorting);
      this._saveSortSettings(sortConfig, currentSettings);
    }

    this._applyPersistedColumnWidths();
    this._resizeTable();
  },

  _sortingConfigMapping(sorting) {
    return _.map(sorting, function(sortColumn) {
      const columnIdx = sortColumn[0];
      const column = this.table.columnsConfig()[columnIdx];
      return [column.attr, sortColumn[1]];
    }, this);
  },

  _getVisibleColumnOrderAndWidth() {
    const columnVisibility = this._getCurrentSettingsState().columnVisibility;

    const visibleColumnsConfig = _.compact(_.map(this.table.columnsConfig(), column => {
      if (column.attr && columnVisibility[column.attr]) {
        return column;
      }
    }));

    return { column_order: this._getCurrentColumnOrderAndWidth(visibleColumnsConfig).toString() };
  },

  _findColumnElementFromId(columnId) {
    return _.find(this.table.columnElements(), colEl => $(colEl).hasClass(Backdraft.Utils.toColumnCSSClass(columnId)));
  },

  _getColumnWidth(column) {
    let width = this._getPersistedColumnWidth(column) || 0;
    const columnElement = this._findColumnElementFromId(column.id);

    // stick with width from persistedSettings on initial load (this.settingsState not defined)
    if (this.settingsState && columnElement) {
      const elementWidth = parseInt(columnElement.style.width);

      if (!this.visibilityChange || (_.isFinite(elementWidth) && elementWidth !== 0)) {
        width = $(columnElement).outerWidth();
      }
    }

    return width;
  },

  _getPersistedColumnWidth(column) {
    if (this.persistedColumnWidths) {
      return this.persistedColumnWidths[column.attr];
    }
  },

  _onPageLengthChange(event) {
    const currentSettings = this._getCurrentSettingsState();
    const pageLength = currentSettings.pageLength;
    const persistedLength = this.persistedSettings.pageLength;

    if (pageLength && persistedLength && persistedLength !== pageLength) {
      this.table.dataTable.trigger("pageLengthChange");
      this._saveSettings({ page_length: pageLength }, currentSettings);
    }
  },

  _onColumnVisibilityChange() {
    this.visibilityChange = true;
    this._resetTableWidth();
    this.table.trigger("draw");
    const currentSettings = this._getCurrentSettingsState();
    if (this.saveOnVisibilityChanges && !_.isEqual(this.settingsState.columnVisibility, currentSettings.columnVisibility)) {
      const changedColumns = this.table._columnManager.visibility.changedAttributes();
      const updates = [];
      _.each(changedColumns, function(visible, colId) {
        const column = this._findColumnById(colId);
        const defaultColumn = this._findDefaultColumnByAttr(column.attr);
        const defaultVisibility = defaultColumn && _.has(defaultColumn, 'visible') ? defaultColumn.visible : true;
        updates.push({
          column_name: column.attr,
          default_value: defaultVisibility ? "1" : "0",
          current_value: visible ? "1" : "0"
        });
      }, this);
      this._saveSettings({ column_visibilities: updates, column_order_and_width: currentSettings.columnOrderAndWidth }, currentSettings);
    } else {
      this._saveColumnWidthChanges();
    }
    this.visibilityChange = false;
  },

  _onColumnOrderChange() {
    const currentSettings = this._getCurrentSettingsState();
    if (!_.isEqual(this.settingsState.columnOrder, currentSettings.columnOrder)) {
      this._saveSettings({ column_order_and_width: currentSettings.columnOrderAndWidth }, currentSettings);
    }
  },

  _onColumnWidthChange(event) {
    if (this.resizingColumnHeader) {
      this.resizingColumnHeader = false;
      this._saveColumnWidthChanges();
    }
  },

  _saveColumnWidthChanges() {
    const currentSettings = this._getCurrentSettingsState();
    if (!_.isEqual(this.settingsState.columnOrderAndWidth, currentSettings.columnOrderAndWidth)) {
      this._saveSettings({ column_order_and_width: currentSettings.columnOrderAndWidth }, currentSettings);
    }
  },

  _resizeTable() {
    // apply table width after render for the persisted column widths to have effect
    // ONLY if on resizableColumns
    if (this.table.resizableColumns) {
      // calculate the widths of rendered columns and resize the table accordingly, considering persisted widths
      const tableWidth = _.reduce(this.table.columnElements(), (totalWidth, columnElement) => totalWidth + parseInt(columnElement.style.width), 0);

      const minWidthToResize = this.table.parent._bulkEnabled() ? BULK_COLUMN_HEADER_DEFAULT_WIDTH : 0;
      if (tableWidth > minWidthToResize) {
        $(this.table.dataTable).css('width', tableWidth);
      }
    }
  },

  _resetTableWidth() {
    this._setBulkColumnHeaderDefaults();
    if (this.table.$('.table-footer-controls').length > 0) {
      const footerWidth = this.table.$('.table-footer-controls').outerWidth(true);
      this.table.$('table').css({ 'width': footerWidth, 'min-width': 'initial' });
    }
  },

  _saveSettings(settingsUpdate, currentSettings, completeCallback) {
    if (this.persistedSettings && this.persistedSettings.updateUrl) {
      $.ajax({
        url: this.persistedSettings.updateUrl,
        method: 'POST',
        dataType: 'json',
        contentType: 'application/json',
        complete() {
          if (completeCallback) {
            completeCallback();
          }
        },
        data: JSON.stringify(_.extend({
          ajax:              "1",
          format:            'json',
          backdraft_request: "1",
          report_type:       this.persistedSettings.reportType
        }, settingsUpdate))
      });
      this._updatePersistedSettings();
    }
    this._cacheSettingsState(currentSettings);
  },

  _setBulkColumnHeaderDefaults() {
    this.table.columnElements('.bulk').css({ 'width': BULK_COLUMN_HEADER_DEFAULT_WIDTH, 'max-width': BULK_COLUMN_HEADER_DEFAULT_WIDTH });
  },

  _getSortSettings() {
    if (this.sortConfigInUrl) {
      return this._readSortFromUrl();
    } else if (this.persistedSettings.sorting) {
      try {
        return JSON.parse(this.persistedSettings.sorting);
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  },

  _saveSortSettings(sortConfig, currentSettings) {
    this._saveSettings({ sorting: JSON.stringify(sortConfig) }, currentSettings);

    if (this.sortConfigInUrl) {
      this._writeSortToUrl(sortConfig);
    }
  },

  _urlParams() {
    return $.deparam(window.location.href.split("?")[1] || "");
  },

  _readSortFromUrl() {
    const params = this._urlParams();

    const sortField = params.sort_field;
    if (sortField) {
      const sortDir = _.has(params, "sort_dir") ? params.sort_dir : "asc";
      return [[sortField, sortDir]];
    } else {
      return [];
    }
  },

  _writeSortToUrl(sortConfig) {
    const params = this._urlParams();

    const cleanedParams = _.omit(params, URL_PARAM_KEYS);

    if (_.isArray(sortConfig) && sortConfig.length) {
      cleanedParams.sort_field = sortConfig[0][0];
      cleanedParams.sort_dir = sortConfig[0][1];
    }

    const currentFragment = History.fragment || "";
    History.navigate(`${currentFragment.split("?")[0]}?${$.param(cleanedParams)}`, {
      trigger: false,
      replace: true
    });
  }
});

export default DataTableSettingsManager;
